import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useDispatch, useSelector } from "react-redux"; 
import { Practice } from "../types";

import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  TrendingUp,
  MessageSquare,
  Database,
  Share2,
  Send,
  DollarSign,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../hooks/Auth/useAuth";
import { fetchPracticeById } from "../redux/practice/practiceSlice";
import { RootState } from "../redux/index";

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
}

interface DashboardProps {
  children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardProps> = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useAuth();

  const { selectedPractice } = useSelector((state: RootState) => state.practices);

  const practiceId = JSON.parse(localStorage.getItem('user') || '{}')?.["custom:id_practice"];
  const userType   = JSON.parse(localStorage.getItem('user') || '{}')?.["custom:user_role"];
  

  // if (userType?.email === "info@therapistdirect.org") {
  //   isAdmin = true;
  // } 


  useEffect(() => {
    if (practiceId) {
      dispatch(fetchPracticeById(practiceId) as any);
    } else {
      console.error("Practice ID is not available");
    } 
  }, [dispatch, practiceId]);

  
  // State to handle sidebar collapse
  const [collapsed, setCollapsed] = useState(false);

  // Automatically collapse the sidebar on smaller screens (e.g., width < 768px)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);



  let navigation: NavItem[] = [];
  if (  userType === "practice_admin") {
    navigation = [
      { name: "Dashboard",    href: "/practice", icon: LayoutDashboard },
      { name: "Clinicians",   href: "/practice/clinicians", icon: Users },
      { name: "Analytics",    href: "/practice/analytics", icon: TrendingUp },
      { name: "Matchmaking",  href: "/practice/matchmaking", icon: MessageSquare },
      { name: "Advertise",    href: "/practice/advertise", icon: DollarSign },
      { name: "Blog",         href: "/practice/blog", icon: FileText },
      { name: "Integrations", href: "/practice/integrations", icon: Database },
      { name: "Workforce",  href: "/practice/workforce", icon: Send },
      { name: "Referrals",    href: "/practice/referrals", icon: Share2 },  
      { name: "Settings",     href: "/practice/settings", icon: Settings }
     
    ];
  } else {
    navigation = [
      { name: "Admin Dashboard",    href: "/admin", icon: LayoutDashboard },
      { name: "Blogs",               href: "/admin/blog", icon: FileText },
      { name: "Practices",          href: "/admin/practices", icon: Database },
      { name: "Modalities",         href: "/admin/modalities", icon: Database },
      { name: "Insurances",         href: "/admin/insurances", icon: Database },
      { name: "Specialties",        href: "/admin/specialties", icon: Database },
      { name: "States",             href: "/admin/states", icon: Database },
      { name: "Settings",           href: "/admin/settings", icon: Settings },
    ];
  }
  // from-purple-600 via-purple-700 to-purple-800

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Ultra-Modern Glassmorphic Sidebar */}
       <div
        className={`
          relative
          backdrop-blur-xl
           
          bg-gradient-to-br from-indigo-600/95 via-purple-700/90 to-slate-800/95
          border-r border-white/10
          shadow-[0_8px_32px_rgba(31,38,135,0.37),inset_0_1px_0_rgba(255,255,255,0.1)]
          text-white 
          sticky top-0 h-screen 
          transition-all duration-500 ease-in-out
          overflow-hidden
          ${collapsed ? "w-20" : "w-72"}
        `}
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -right-1/2 w-96 h-96 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-1/2 -left-1/2 w-96 h-96 bg-gradient-to-tr from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        {/* Header Section */}
        <div className="relative z-10 flex items-center justify-between p-6 border-b border-white/10 backdrop-blur-sm">
          {!collapsed && (
            <div className="space-y-1">
              <h2 className="text-xl font-bold tracking-wide bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-200 to-blue-200 drop-shadow-sm">
                {selectedPractice?.name || "Therapist Direct"}
              </h2>
              {selectedPractice?.DBA && (
                <p className="text-xs text-purple-200/80 font-medium tracking-wider uppercase">
                  {selectedPractice.DBA}
                </p>
              )}
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="
              group relative p-2.5 rounded-xl 
              bg-white/10 hover:bg-white/20 
              border border-white/10 hover:border-white/20
              backdrop-blur-sm
              transition-all duration-300 
              hover:shadow-lg hover:shadow-purple-500/25
              hover:scale-105
            "
          >
            {collapsed ? (
              <ChevronRight className="h-5 w-5 text-white group-hover:text-purple-200 transition-colors" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-white group-hover:text-purple-200 transition-colors" />
            )}
          </button>
        </div>

        {/* Navigation Section */}
        <nav className="relative z-10 mt-6 px-4 space-y-2">
          {navigation.map((item, index) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  group relative flex items-center px-2 py-3.5 
                  text-sm font-medium rounded-xl
                  transition-all duration-300 ease-out
                  transform hover:scale-[1.02]
                  ${isActive
                    ? `
                      bg-gradient-to-r from-white/20 to-white/10 
                      text-white shadow-lg shadow-purple-500/25
                      border border-white/20
                      backdrop-blur-sm
                    `
                    : `
                      text-purple-100/90 hover:text-white
                      hover:bg-gradient-to-r hover:from-white/10 hover:to-white/5
                      hover:shadow-lg hover:shadow-purple-500/10
                      hover:border hover:border-white/10
                      hover:backdrop-blur-sm
                    `
                  }
                `}
                style={{
                  animationDelay: `${index * 50}ms`
                }}
              >
                {/* Active indicator */}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-purple-400 to-blue-400 rounded-r-full shadow-lg shadow-purple-400/50"></div>
                )}
                
                <div className={`
                  relative z-10 rounded-lg
                  ${isActive 
                    ? "bg-gradient-to-br from-purple-500/30 to-blue-500/30 shadow-inner" 
                    : "group-hover:bg-white/10"
                  }
                  transition-all duration-300
                `}>
                  <Icon className={`h-5 w-5 flex-shrink-0 transition-all duration-300 ${
                    isActive 
                      ? "text-white drop-shadow-sm" 
                      : "text-purple-200/80 group-hover:text-white group-hover:scale-110"
                  }`} />
                </div>
                
                {!collapsed && (
                  <span className={`
                    ml-4 truncate font-medium tracking-wide
                    ${isActive ? "text-white" : "text-purple-100/90 group-hover:text-white"}
                    transition-colors duration-300
                  `}>
                    {item.name}
                  </span>
                )}

                {/* Hover glow effect */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-400/0 via-purple-400/10 to-blue-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
            );
          })}
        </nav>

        {/* Bottom decorative element */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent rounded-full"></div>
      </div>

      {/* Main Content */}
      <div className="flex-1 relative">
        {children}
      </div>
    </div>
  );
};
